// ── Calendar helpers: bounded, inclusive date-range presentation ───────────
// The page requests the handwritten `/calendar` read with an inclusive from/to
// window. The API applies scope and date predicates; these helpers only bucket
// the returned sessions and format their existing local wall times.

import type { CoachingSessionDto } from "../../services/coaching-dtos";

export interface CalendarDateRange {
  [key: string]: string;
  from: string;
  to: string;
}

export function currentMonthDateRange(now: Date = new Date()): CalendarDateRange {
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();
  const pad = (value: number) => String(value).padStart(2, "0");
  const from = `${year}-${pad(month + 1)}-01`;
  const to = `${year}-${pad(month + 1)}-${pad(new Date(Date.UTC(year, month + 1, 0)).getUTCDate())}`;
  return { from, to };
}

export interface CalendarDayBucket {
  /** YYYY-MM-DD key, or "undated" for sessions without a parseable start. */
  dateKey: string;
  /** Display label (e.g. "Sun, Mar 8") or "No scheduled date". */
  label: string;
  sessions: CoachingSessionDto[];
}

/** Parse the YYYY-MM-DD portion of a session start (ISO or date-only). */
export function sessionDateKey(session: CoachingSessionDto): string | null {
  const raw = (session.starts_at ?? "").trim();
  if (!raw) return null;
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[1]}-${match[2]}-${match[3]}` : null;
}

function labelForDateKey(dateKey: string): string {
  if (dateKey === "undated") return "No scheduled date";
  // Construct at UTC noon to avoid timezone roll-back on the date-only key.
  const date = new Date(`${dateKey}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return dateKey;
  return new Intl.DateTimeFormat("en", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC"
  }).format(date);
}

/**
 * Group sessions into day buckets, sorted ascending by date (undated last). The
 * input is the role-scoped list verbatim; this is pure presentation bucketing.
 */
export function bucketSessionsByDay(sessions: CoachingSessionDto[]): CalendarDayBucket[] {
  const buckets = new Map<string, CoachingSessionDto[]>();
  for (const session of sessions) {
    const key = sessionDateKey(session) ?? "undated";
    const list = buckets.get(key) ?? [];
    list.push(session);
    buckets.set(key, list);
  }
  return Array.from(buckets.entries())
    .sort(([a], [b]) => {
      if (a === "undated") return 1;
      if (b === "undated") return -1;
      return a.localeCompare(b);
    })
    .map(([dateKey, daySessions]) => ({
      dateKey,
      label: labelForDateKey(dateKey),
      sessions: daySessions.slice().sort(sortByStart)
    }));
}

function sortByStart(a: CoachingSessionDto, b: CoachingSessionDto): number {
  return (a.starts_at ?? "").localeCompare(b.starts_at ?? "");
}

/** Time-of-day label (e.g. "14:00") from an ISO start, honest "—" when absent. */
export function sessionTimeLabel(session: CoachingSessionDto): string {
  const raw = (session.starts_at ?? "").trim();
  const match = raw.match(/T(\d{2}:\d{2})/);
  return match ? match[1] : "—";
}
